import { useCallback, useEffect, useRef, useState } from "react";
import { GTVMessage } from "../util/types";
import { useAuth } from "../contexts/AuthContext";
import {
  GAMENIGHTTV_CAST_CHANNEL,
  PING_TIMER_INTERVAL_MS,
  GAMENIGHTTV_CAST_APP_ID,
  TESTING_ENABLED,
} from "../util/consts";
import { useCastSenderContext } from "../contexts/CastSenderContext";

declare const cast: any; // <-- important bit. you are using google's code here

export function useCastSender(
  senderSessionId: string,
  sendSocketMessage: (message: GTVMessage) => void | Promise<void>,
): {
  castConnected: boolean;
  hostAuthorized: boolean;
} {
  const {
    castSenderLoaded,
    setCastSenderLoaded,
    castFrameworkLoaded,
    setCastFrameworkLoaded,
  } = useCastSenderContext();
  const sessionIdRef = useRef(senderSessionId);
  const [castConnected, setCastConnected] = useState(false);
  const { isAuthenticated } = useAuth();
  // const connectHostInterval = useRef<NodeJS.Timeout | null>(null);
  const pingInterval = useRef<NodeJS.Timeout | null>(null);
  // const silentMp3Interval = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);
  const castSenderLog = useCallback(
    async (type: "Error" | "Info" | "Warning", message: string) => {
      switch (type) {
        case "Error":
          console.error("CastSender: ", message);
          break;
        case "Warning":
          console.warn("CastSender: ", message);
          break;
        default:
          console.log("CastSender: ", message);
      }
      // Uncomment to enable cast sender logs to be sent to the server for debugging
      await sendSocketMessage({
        Type: "CastSenderLog",
        Payload: {
          Level: type,
          Message: message,
        },
      });
    },
    [sendSocketMessage],
  );
  const onCastSessionDisconnected = useCallback(async () => {
    await castSenderLog("Info", "Cast session disconnected");
    setHostAuthorized((prev) => !prev && false);
    if (pingInterval.current) {
      clearInterval(pingInterval.current);
      pingInterval.current = null;
    }
    const context = cast.framework.CastContext.getInstance();
    const session = context.getCurrentSession();
    if (session) {
      session.endSession(true);
    }
  }, [pingInterval, castSenderLog]);
  const sendToCastReceiver = useCallback(
    async (message: GTVMessage) => {
      try {
        const context = cast.framework.CastContext.getInstance();
        const session = context.getCurrentSession();
        if (session) {
          if (message.Type !== "Ping") {
            await castSenderLog(
              "Info",
              "Sending message to cast receiver: " + JSON.stringify(message),
            );
          }
          session.sendMessage(
            GAMENIGHTTV_CAST_CHANNEL,
            JSON.stringify(message),
          );
        } else {
          await castSenderLog(
            "Warning",
            "No active cast session found, cannot send message: " +
              JSON.stringify(message) +
              ". Clearing the session states.",
          );
          onCastSessionDisconnected();
        }
      } catch (error) {
        await castSenderLog(
          "Error",
          "Error sending message to cast receiver: " + error,
        );
      }
    },
    [onCastSessionDisconnected, castSenderLog],
  );
  // This is step 1 of 3 to connect the host to the cast receiver.
  // The sender will send a ConnectHost message to the receiver, which will
  // send a ConnectHost message to the server with the session ID.
  // The server will then send a session ID back to the Host to complete the connection.
  const onAuthenticatedToCast = useCallback(async () => {
    await castSenderLog(
      "Info",
      "Authenticated to cast sender, setting up cast button and options",
    );
    try {
      //casting options
      const castButton = document.getElementById("cast-button");
      if (castButton && castButton.childElementCount > 0) {
        return;
      }
      let castLauncher = document.getElementById("google-cast-launcher");
      if (!castLauncher) {
        castLauncher = document.createElement("google-cast-launcher");
        // castLauncher.id = "google-cast-launcher";
      }
      if (castButton && castLauncher) {
        try {
          castButton.appendChild(castLauncher);
          castButton.style.display = "block";
          castLauncher.style.display = "block";

          castLauncher.style.visibility = "visible";
          castLauncher.style.display = "block";
        } catch (err) {
          await castSenderLog(
            "Error",
            "Error adding cast button to DOM: " + err,
          );
        }
      }
    } catch (err) {
      await castSenderLog("Error", "Error setting up cast button: " + err);
    }
  }, [castSenderLog]);
  useEffect(() => {
    if (isAuthenticated) {
      onAuthenticatedToCast();
    } else {
      castSenderLog("Info", "User is not authenticated, hiding cast button");
      const castButton = document.getElementById("cast-button");
      if (castButton) {
        castButton.style.visibility = "hidden";
        castButton.style.display = "none";
      }
    }
  }, [isAuthenticated, onAuthenticatedToCast, castSenderLog]);
  const [hostAuthorized, setHostAuthorized] = useState(false);
  const onCastSessionReady = useCallback(async () => {
    // Connect to host
    await castSenderLog(
      "Info",
      "Cast session is ready, sending ConnectHost message to cast receiver" +
        " with session ID: " +
        sessionIdRef.current,
    );
    sendToCastReceiver({
      Type: "ConnectHost",
      SessionId: sessionIdRef.current,
    });
    const context = cast.framework.CastContext.getInstance();
    const session = context.getCurrentSession();
    if (session) {
      await castSenderLog(
        "Info",
        "Cast connected for session. Setting up post connection logic for cast receiver",
      );
      const pingIntervals = () => {
        return setInterval(async () => {
          try {
            // If the host is authorized, we send a ping message with the current timestamp,
            const message: GTVMessage = {
              Type: "Ping",
              Payload: {
                Timestamp: Date.now(),
              },
            };

            sendToCastReceiver(message);
          } catch (error) {
            await castSenderLog(
              "Error",
              "Error sending ping message: " + error,
            );
          }
        }, PING_TIMER_INTERVAL_MS);
      };
      if (pingInterval.current) {
        clearInterval(pingInterval.current);
      }
      pingInterval.current = pingIntervals();
      // silentMp3Interval.current = silentMp3Intervals();
      session.addMessageListener(
        GAMENIGHTTV_CAST_CHANNEL,
        async (customEvent: any) => {
          try {
            await castSenderLog(
              "Info",
              "Received message from cast receiver: " +
                JSON.stringify(customEvent),
            );
            const message: GTVMessage = JSON.parse(customEvent);
            switch (message.Type) {
              case "HostAuthorized":
                setHostAuthorized(true);
                break;
              default:
                await castSenderLog(
                  "Warning",
                  "Unhandled message type from cast receiver: " + message.Type,
                );
                break;
            }
          } catch (error) {
            await castSenderLog(
              "Error",
              "Error parsing message from cast receiver: " + error,
            );
          }
        },
      );
    } else {
      await castSenderLog(
        "Warning",
        "No active cast session found, cannot set up message listener",
      );
    }
  }, [castSenderLog, sendToCastReceiver, pingInterval, sessionIdRef]);
  const configureSenderCasting = useCallback(async () => {
    await castSenderLog("Info", "Setting up Google Cast for Player...");
    window.__onGCastApiAvailable = async (
      available: boolean,
      reason: string | undefined,
    ) => {
      await castSenderLog(
        "Info",
        "Is Google Cast API available: " + available + ", Reason: " + reason,
      );
      if (available) {
        const context = cast.framework.CastContext.getInstance();
        context.setOptions({
          receiverApplicationId: GAMENIGHTTV_CAST_APP_ID,
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        });
        context.addEventListener(
          cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
          async (event: any) => {
            if (
              event.sessionState ===
                cast.framework.SessionState.SESSION_STARTED ||
              event.sessionState === cast.framework.SessionState.SESSION_RESUMED
            ) {
              setCastConnected(true);
              await castSenderLog("Info", "Cast session connected");
              await onCastSessionReady();
            } else if (
              event.sessionState === cast.framework.SessionState.SESSION_ENDED
            ) {
              setCastConnected(false);
              await castSenderLog("Warning", "Cast session disconnected");
              await onCastSessionDisconnected();
            }
          },
        );
        await castSenderLog("Info", "Google Cast Instantiated!");
      }
    };
  }, [onCastSessionReady, onCastSessionDisconnected, castSenderLog]);
  const loadGoogleCastSender = useCallback(() => {
    if (castSenderLoaded) {
      configureSenderCasting();
      return;
    }
    const script = document.createElement("script");
    script.src = "//www.gstatic.com/cv/js/sender/v1/cast_sender.js";
    script.type = "text/javascript";
    script.onload = () => {
      setCastSenderLoaded(true);
      configureSenderCasting();
    };
    document.body.appendChild(script);
  }, [configureSenderCasting, castSenderLoaded, setCastSenderLoaded]);
  const loadGoogleCastSenderFramework = useCallback(() => {
    if (castFrameworkLoaded) {
      loadGoogleCastSender();
      return;
    }
    // Load Google Cast API Framework First
    const script = document.createElement("script");
    script.src = "//www.gstatic.com/cast/sdk/libs/sender/1.0/cast_framework.js";
    script.type = "text/javascript";
    script.onload = () => {
      loadGoogleCastSender();
      setCastFrameworkLoaded(true);
    };
    document.body.appendChild(script);
  }, [loadGoogleCastSender, castFrameworkLoaded, setCastFrameworkLoaded]);

  useEffect(() => {
    if (isFirstRender.current && !TESTING_ENABLED) {
      isFirstRender.current = false;
      loadGoogleCastSenderFramework();
    }
  }, [loadGoogleCastSenderFramework]);
  useEffect(() => {
    castSenderLog("Info", "Session ID updated: " + senderSessionId);
    sessionIdRef.current = senderSessionId;
  }, [senderSessionId, castSenderLog]);
  return {
    castConnected,
    hostAuthorized,
  };
}
